/* eslint-disable react-refresh/only-export-components -- shadcn-style variant/helper exports co-located with their components */
import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Slot } from "radix-ui"
import { ArrowPathIcon } from "@heroicons/react/24/outline"

import { cn } from "@/lib/utils"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"

/**
 * Story 24.2 — the one Button (DESIGN.md › Components › Button). Roles only.
 *
 * - Radius: DESIGN `md` (6px) is `--radius` here, which Tailwind exposes as
 *   `rounded-lg`; `rounded-md` is 4px in this app. So buttons are
 *   `rounded-lg`.
 * - Hover fills are plain `hover:` (and `floating:hover:`), the same
 *   modifier callers use, so a caller's `hover:bg-…` replaces the variant's
 *   through tailwind-merge. An `aria-disabled` button (still focusable and
 *   hoverable, for its tooltip) never looks pressable: each variant pins its
 *   rest fill under `aria-disabled:hover:`.
 * - Focus is the shared `focus-ring` outline, never a box-shadow ring.
 */
const buttonVariants = cva(
  "inline-flex shrink-0 items-center justify-center gap-2 rounded-lg type-label whitespace-nowrap transition-colors motion-reduce:transition-none focus-ring disabled:pointer-events-none disabled:opacity-40 aria-disabled:opacity-40 aria-disabled:cursor-not-allowed aria-invalid:border-danger [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-5",
  {
    variants: {
      variant: {
        primary:
          "bg-action text-action-ink hover:bg-action-hover aria-disabled:hover:bg-action",
        secondary:
          "border border-line-strong bg-surface text-ink hover:bg-hover aria-disabled:hover:bg-surface",
        ghost:
          "text-ink hover:bg-hover floating:hover:bg-hover-on-raised aria-disabled:hover:bg-transparent floating:aria-disabled:hover:bg-transparent",
        danger: "bg-danger text-danger-ink",
        link: "text-link link-standalone",
      },
      size: {
        default: "min-h-11 px-4 py-2 has-[>svg]:px-3",
        xs: "min-h-6 gap-1 px-2 has-[>svg]:px-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "min-h-8 gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "min-h-12 px-6 has-[>svg]:px-4",
        icon: "size-11",
        "icon-xs": "size-6 [&_svg:not([class*='size-'])]:size-3",
        "icon-sm": "size-8",
        "icon-lg": "size-12",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "default",
    },
  }
)

type ButtonVariant = NonNullable<VariantProps<typeof buttonVariants>["variant"]>

type ButtonProps = React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
    /** Busy: the label stays, a leading spinner shows, presses do nothing. */
    loading?: boolean
    /**
     * Disabled with a reason: `aria-disabled` (never `disabled`, and it wins
     * over a `disabled` prop), stays tabbable, presses are swallowed, and the
     * reason is both the button's description and a tooltip (hover, focus,
     * or a ~500ms touch long-press).
     */
    disabledReason?: string
  }

const LONG_PRESS_MS = 500

/**
 * Make the reason Tooltip's trigger skip its own handler for this one event,
 * without marking the event prevented for anyone else.
 *
 * Radix runs its trigger handler right after ours and skips it when
 * `event.defaultPrevented` is true. A real `preventDefault()` would also be
 * seen by every ancestor the event bubbles to (a Radix menu item's
 * pointer-move, a roving-focus group's focus). So `defaultPrevented` reads
 * `true` exactly once — Radix's check, which is the very next read — and is
 * then restored. The native event is never touched.
 *
 * This leans on Radix's `composeEventHandlers` reading `defaultPrevented`
 * once, straight after our handler (react-tooltip 1.2.8). Re-check it on a
 * Radix upgrade; `button.test.tsx` ("never broadcasts") pins the behaviour.
 */
function skipNextRadixHandler(event: React.SyntheticEvent) {
  const original = event.defaultPrevented
  Object.defineProperty(event, "defaultPrevented", {
    configurable: true,
    enumerable: true,
    get() {
      Object.defineProperty(event, "defaultPrevented", {
        configurable: true,
        enumerable: true,
        writable: true,
        value: original,
      })
      return true
    },
  })
}

function Button({
  className,
  variant = "primary",
  size = "default",
  asChild = false,
  loading = false,
  disabledReason,
  disabled,
  children,
  onClick,
  onFocus,
  onPointerMove,
  onPointerDown,
  onPointerUp,
  onPointerLeave,
  onPointerCancel,
  onContextMenu,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot.Root : "button"
  const reasonId = React.useId()
  const [reasonOpen, setReasonOpen] = React.useState(false)
  const pressTimer = React.useRef<number | undefined>(undefined)
  // The open reason came from a touch long-press (until it closes). Only a
  // touch press may suppress the context menu; a keyboard or mouse menu on a
  // button whose reason is showing is never blocked.
  const longPressed = React.useRef(false)
  const hasReason = Boolean(disabledReason)

  const cancelPress = () => {
    window.clearTimeout(pressTimer.current)
    pressTimer.current = undefined
  }

  React.useEffect(() => () => window.clearTimeout(pressTimer.current), [])

  // The reason went away: no pending long-press, no tooltip left open.
  React.useEffect(() => {
    if (!hasReason) {
      window.clearTimeout(pressTimer.current)
      pressTimer.current = undefined
      longPressed.current = false
      setReasonOpen(false)
    }
  }, [hasReason])

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (hasReason || loading) {
      // Swallow the press. preventDefault also stops an `asChild` link from
      // navigating, a submit button from submitting, and (for the reason
      // tooltip) Radix's trigger from closing the tooltip on a keyboard
      // click. A mouse press has already closed it by then: Radix closes any
      // tooltip on a press of its trigger, and hover or focus brings it back.
      event.preventDefault()
      return
    }
    onClick?.(event)
  }

  const spinner = loading ? (
    <ArrowPathIcon key="spinner" aria-hidden="true" data-slot="button-spinner" className="motion-safe:animate-spin" />
  ) : null

  const content = !asChild ? (
    <>
      {spinner}
      {children}
      {hasReason && (
        // Inside the button, so it never becomes a group's :last-child;
        // hidden content joins no accessible name.
        <span id={reasonId} hidden>
          {disabledReason}
        </span>
      )}
    </>
  ) : loading ? (
    // Slottable puts the spinner inside the child element, before its own
    // children.
    [spinner, <Slot.Slottable key="child">{children}</Slot.Slottable>]
  ) : (
    // No spinner to place, so the child goes to the Slot untouched. Through
    // Slottable its children arrive as an array, and a child that is itself
    // `asChild` renders an array as nothing.
    children
  )

  // One tree shape whether or not there is a reason, so adding or removing a
  // reason never remounts the button (focus stays put). Without a reason the
  // Tooltip is inert: it never opens, and the trigger's hover/focus open
  // requests are stopped so they never broadcast "a tooltip opened" and close
  // someone else's.
  return (
    <>
      <Tooltip
        open={hasReason && reasonOpen}
        onOpenChange={(next) => {
          if (!next) longPressed.current = false
          setReasonOpen(hasReason && next)
        }}
      >
        <TooltipTrigger asChild>
          <Comp
            data-slot="button"
            data-variant={variant}
            data-size={size}
            className={cn(
              buttonVariants({ variant, size, className }),
              hasReason && "[-webkit-touch-callout:none] select-none"
            )}
            aria-busy={loading ? true : undefined}
            {...props}
            // The caller's (or an outer Radix trigger's) data-state wins over
            // the reason tooltip's.
            data-state={(props as { "data-state"?: string })["data-state"]}
            disabled={hasReason ? undefined : disabled}
            aria-disabled={hasReason ? "true" : props["aria-disabled"]}
            aria-describedby={
              hasReason
                ? [reasonId, props["aria-describedby"]].filter(Boolean).join(" ")
                : props["aria-describedby"]
            }
            onClick={handleClick}
            onFocus={(event: React.FocusEvent<HTMLButtonElement>) => {
              onFocus?.(event)
              if (!hasReason) skipNextRadixHandler(event)
            }}
            onPointerMove={(event: React.PointerEvent<HTMLButtonElement>) => {
              onPointerMove?.(event)
              if (!hasReason) skipNextRadixHandler(event)
            }}
            onPointerDown={(event: React.PointerEvent<HTMLButtonElement>) => {
              onPointerDown?.(event)
              if (!hasReason || event.pointerType !== "touch") return
              cancelPress()
              pressTimer.current = window.setTimeout(() => {
                pressTimer.current = undefined
                longPressed.current = true
                setReasonOpen(true)
              }, LONG_PRESS_MS)
            }}
            onPointerUp={(event: React.PointerEvent<HTMLButtonElement>) => {
              onPointerUp?.(event)
              cancelPress()
            }}
            onPointerLeave={(event: React.PointerEvent<HTMLButtonElement>) => {
              onPointerLeave?.(event)
              if (event.pointerType === "touch") cancelPress()
            }}
            onPointerCancel={(event: React.PointerEvent<HTMLButtonElement>) => {
              onPointerCancel?.(event)
              cancelPress()
            }}
            onContextMenu={(event: React.MouseEvent<HTMLButtonElement>) => {
              onContextMenu?.(event)
              if (!hasReason) return
              // A long-press on touch raises the OS context menu; the reason
              // tooltip is the long-press's job here. The timer and
              // `longPressed` cover browsers whose contextmenu event carries
              // no pointer type; the timer only ever runs for a touch press.
              const pointerType = (event.nativeEvent as PointerEvent).pointerType
              if (pointerType === "touch" || pressTimer.current !== undefined || longPressed.current) {
                event.preventDefault()
              }
            }}
          >
            {content}
          </Comp>
        </TooltipTrigger>
        {hasReason && <TooltipContent>{disabledReason}</TooltipContent>}
      </Tooltip>
      {hasReason && asChild && (
        <span id={reasonId} hidden>
          {disabledReason}
        </span>
      )}
    </>
  )
}

export { Button, buttonVariants }
export type { ButtonProps, ButtonVariant }
